/* eslint-disable react-hooks/exhaustive-deps */
// NOTE: This in-authorization Expenses tab conceptually reuses Finance.Expenses (same as the main
// Expenses module) but is not currently gated at this container level - flagged for follow-up.

import { Box, ThemeProvider, createTheme } from '@mui/material';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';

import FullscreenSpinner from 'modules/common/fullscreenSpinner.component';

import { COLOR_PRIMARY, COLOR_SECONDARY, COLOR_WARNING } from 'styles/theme';
import {
  getAuthClientPolicies,
  getAuthorizationSummaries,
  getAuthorizationSummary,
  getEmployeeInfo,
  setAuthId
} from '../../store/actions/authorizations.actions';

import { getClientDetails, setClientId, setLoadingComplete } from 'modules/clients/clients.actions';
import { showToast } from 'modules/layout/layout.actions';
import Expenses from './expenses.component';
import { getExpenseCodesByClientId, getVendors } from 'modules/authorizations/store/actions/dataManagement.actions';
import { fetchAuthDetails, fetchEnumerations, fetchExpenseCodes, fetchVendors } from 'modules/authorizations/utils/fetchDataDetailsService';
import {
  getAllCountries,
  getAllCurrencies,
  getAllEnumerations,
  getAllProvinces,
  getAllSaleTypes,
  getAllWeightTypes
} from 'modules/enumerations/store/enumerationActions';
import { getApprovedLineItemSummaries, getExpenseByAuthorization } from 'modules/expenses/store/expenses.actions';
import { Permissions } from 'modules/layout/permissions';
import { hasPermission } from 'modules/layout/permissionService';

const SHORT_TERM_ASSIGNMENT_MOVE_TYPE_ID = '83BBE794-E171-403E-B545-23508B017354';

const ExpensesView = props => {
  const { authorizationDetails, match, authorizationSummaries, clients, enumerations, expenseCodes, vendors } = props;
    const isShortTermAssignment = authorizationDetails?.authorizationDetails?.moveTypeId?.toUpperCase() === SHORT_TERM_ASSIGNMENT_MOVE_TYPE_ID;
  
    const calcAccess = () => {
      if (hasPermission('File.FullAccess') || hasPermission('File.Edit')) {
        return false;
      }
      else if(isShortTermAssignment && hasPermission(Permissions.File.STA.Edit)) {
        return false;
      }
      else {
        return true;
      }
    };
  
    const isReadOnly = calcAccess();

  const clientsArray = useMemo(() => (Array.isArray(clients) ? clients : Object.values(clients ?? {})), [clients]);

  const normalizedClients = useMemo(
    () =>
      (clientsArray ?? []).map(c => ({
        clientRecordId: c.clientRecordId ?? c.id ?? c.clientId,
        legalName: c.legalName ?? c.clientName ?? c.description ?? c.shortName ?? '',
        id: c.clientRecordId ?? c.id ?? c.clientId,
        description: c.legalName ?? c.clientName ?? c.description ?? '',
        clientId: c.clientRecordId ?? c.id ?? c.clientId,
        clientName: c.legalName ?? c.clientName ?? c.description ?? ''
      })),
    [clientsArray]
  );

  const theme = createTheme({
    palette: { primary: COLOR_PRIMARY, secondary: COLOR_SECONDARY, warning: COLOR_WARNING }
  });

  const [isLoading, setIsLoading] = useState(false);

  const authId = match?.params?.id;
  const details = authorizationDetails ? authorizationDetails[authId] : null;
  const expenseLineItems = details && details.expenses ? details.expenses.expensesLineItems : [];
  const voucherLineItems = details && details.expenses ? details.expenses.vouchers : [];
  const recurringPayables = details && details.expenses ? details.expenses.recurringPayables : [];

  useEffect(() => {
    const run = async () => {
      setIsLoading(true);
      try {
        if (authId != null) props.setAuthId(authId);
        await fetchAuthDetails(details, authId, props);
        await fetchEnumerations(props);
        await fetchVendors(vendors, props);
        if (!details || !Array.isArray(details.expenses)) {
          await props.getExpenseByAuthorization(authId);
        }
        if (!details || !Array.isArray(details.approvedLineItems)) {
          await props.getApprovedLineItemSummaries(authId);
        }
        let clientRecordId = details?.authorizationDetails?.clientId;
        if (!clientRecordId) {
          const authSummaryResp = await props.getAuthorizationSummary(authId);
          clientRecordId = authSummaryResp?.response?.authorizationDetails?.clientId;
        }

        let expenseCodeClientId = null;
        if (clientRecordId) {
          props.setClientId(clientRecordId);
          const clientResp = await props.getClientDetails(clientRecordId);
          expenseCodeClientId = clientResp?.response?.clientDetails?.clientId || clientResp?.response?.clientId || null;
        }
        await fetchExpenseCodes(expenseCodes, props, expenseCodeClientId);
      } finally {
        props.setLoadingComplete();
        setIsLoading(false);
      }
    };
    run();
  }, [authId]);

  return (
    <ThemeProvider theme={theme}>
      <Box>
        {!isLoading && details && (
          <Expenses
            details={details}
            id={authId}
            history={props.history}
            authorizationSummaries={authorizationSummaries}
            enumerations={enumerations && enumerations?.allEnums ? enumerations.allEnums : {}}
            clients={normalizedClients}
            expenseLineItems={expenseLineItems || []}
            voucherLineItems={voucherLineItems || []}
            recurringPayables={recurringPayables || []}
            approvedLineItems={details?.approvedLineItems || []}
            expenseCodes={expenseCodes || []}
            vendors={vendors || []}
            isReadOnly={isReadOnly}
          />
        )}
        {isLoading && <FullscreenSpinner />}
      </Box>
    </ThemeProvider>
  );
};

ExpensesView.propTypes = {
  authorizationDetails: PropTypes.object,
  match: PropTypes.object.isRequired,
  enumerations: PropTypes.object,
  authorizationSummaries: PropTypes.array,
  unmappedClients: PropTypes.array,
  history: PropTypes.object,
  setAuthId: PropTypes.func.isRequired,
  setLoadingComplete: PropTypes.func.isRequired,
  getExpenseByAuthorization: PropTypes.func.isRequired
};

const mapStateToProps = state => {
  const { authorizationDetails, isLoading, authorizationSummaries, vendors, expenseCodes } = state.authorizations;
  const { unmappedClients, clients, clientsInfo } = state.clients;
  const enumerations = state.enumerations;
  return {
    authorizationDetails,
    isLoading,
    authorizationSummaries,
    unmappedClients,
    vendors,
    enumerations,
    clients,
    expenseCodes,
    clientsInfo
  };
};

export default compose(
  connect(mapStateToProps, {
    getAuthClientPolicies,
    getEmployeeInfo,
    setAuthId,
    setLoadingComplete,
    showToast,
    getAuthorizationSummaries,
    getVendors,
    getAuthorizationSummary,
    getAllEnumerations,
    getAllSaleTypes,
    getAllWeightTypes,
    getAllProvinces,
    getAllCurrencies,
    getAllCountries,
    getExpenseByAuthorization,
    getApprovedLineItemSummaries,
    getExpenseCodesByClientId,
    getClientDetails,
    setClientId
  })
)(ExpensesView);
